import axios from 'axios';
import { getApiUrl } from '../config/environment';

// Configurar baseURL correta para Quiz Funnel
const getQuizFunnelApiUrl = () => {
  return getApiUrl();
};

const api = axios.create({
  baseURL: getQuizFunnelApiUrl(),
  timeout: 30000, // 30 segundos - aumentado para evitar timeout em queries mais lentas
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export interface QuizPayload {
  leadUUID: string;
  timestamp: string;
  etapa: string;
  pergunta: string;
  resposta: string | number | string[] | { [key: string]: any };
  tipoPergunta: string;
  questionIndex: number;
}

export interface QuizLeadSummary {
  id: string;
  lead_uuid: string;
  contact_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  current_etapa: string | null;
  is_completed: boolean;
  completed_at: string | null;
  abandoned_at: string | null;
  last_activity_at: string;
  created_at: string;
  total_responses: number;
  last_response_at: string | null;
  first_response_at: string | null;
  tempo_ultima_resposta: number | null;
  tempo_primeira_resposta: number | null;
  tempo_total_funil: number | null;
  nome: string | null;
  whatsapp: string | null;
  email: string | null;
  metadata?: {
    motivo_abandono?: string;
    progresso?: {
      respondidas: number;
      total: number;
      percentual: number;
    };
    tempo_quiz_segundos?: number;
    status?: string;
    whatsapp_messages?: Array<{
      sent_at: string;
      message: string;
    }>;
    last_whatsapp_sent_at?: string;
  };
  respostas: Array<{
    etapa: string;
    pergunta: string;
    resposta: any;
    tipo_pergunta: string;
    timestamp: string;
    tempo_no_funil?: number;
  }>;
}

export interface QuizFunnelStats {
  totalLeads: number;
  completedLeads: number;
  abandonedLeads: number;
  etapaDistribution: Array<{ etapa: string; count: number }>;
  averageCompletionTime: number | null; // em minutos
  averageAbandonmentTime: number | null; // em minutos
}

export interface ListLeadsParams {
  limit?: number;
  offset?: number;
  isCompleted?: boolean;
  etapa?: string;
  search?: string;
}

class QuizFunnelService {
  private static baseUrl = 'quiz-funnel';

  /**
   * Envia um payload recebido do quiz (para uso em webhooks)
   */
  static async sendPayload(payload: QuizPayload) {
    const response = await api.post(`${this.baseUrl}/payload`, payload);
    return response.data;
  }

  /**
   * Lista todos os leads do quiz
   */
  static async listLeads(params: ListLeadsParams = {}) {
    const response = await api.get(`${this.baseUrl}/leads`, { params });
    return response.data;
  }

  /**
   * Busca detalhes de um lead específico
   */
  static async getLeadDetails(leadUUID: string) {
    const response = await api.get(`${this.baseUrl}/leads/${leadUUID}`);
    return response.data;
  }

  /**
   * Busca estatísticas do funil
   */
  static async getStats(): Promise<{ success: boolean; data: QuizFunnelStats }> {
    const response = await api.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  /**
   * Marca uma mensagem WhatsApp como enviada para um lead
   */
  static async markMessageSent(leadUUID: string, message: string): Promise<{ success: boolean; error?: string }> {
    const response = await api.post(`${this.baseUrl}/leads/${leadUUID}/mark-message-sent`, { message });
    return response.data;
  }

  /**
   * Deleta um lead do quiz
   */
  static async deleteLead(leadUUID: string): Promise<{ success: boolean; error?: string }> {
    const response = await api.delete(`${this.baseUrl}/leads/${leadUUID}`);
    return response.data;
  }
}

export default QuizFunnelService;

